import {
  Box,
  Icon,
  Image,
  Theme,
  Typography,
  useTheme,
} from "@outschool/backpack";
import {
  OSSubscriptions,
  centsPerLearnerPerMeeting,
} from "@outschool/business-rules";
import * as FilestackUrls from "@outschool/filestack-urls";
import { ActivityListing_ListingFragment } from "@outschool/gql-frontend-generated";
import { faBirthdayCake, faCalendar, faUsersClass } from "@outschool/icons";
import { addParamsToUrl } from "@outschool/routes";
import {
  measurePerformance,
  useLogSearchImpression,
} from "@outschool/ui-analytics";
import {
  CreditIcon,
  UserHeadshotImage,
  TrackedButton,
} from "@outschool/ui-components-shared";
import { TrackingProperties } from "@outschool/ui-legacy-component-library";
import {
  useDevicePixelRatio,
  useLinkComponent,
  useNavigation,
} from "@outschool/ui-utils";
import React from "react";

import usePriceDetailsString from "../hooks/usePriceDetailsString";
import { SaveHeart, useIsActivitySavedByCurrentUser } from "../Saving";
import useActivityString from "../translations/useActivityString";
import { SectionSearchFilters } from "../utils";
import { isOneOnOne } from "../utils/Activity";
import { ActivityListingProps } from "./ActivityListing";
import {
  LeaderDetailsListing,
  LeaderDetailsMobileListing,
} from "./components/LeaderDetails";
import { NextStartTime } from "./NextStartTime";
import { useTranslation } from "@outschool/localization";

function trackingObject(
  activity_uid: string,
  trackingProperties: Record<any, any>
) {
  return {
    trackingName: "Activity Impression",
    sharedProperties: {
      activity_uid,
      ...trackingProperties,
    },
    trackingTouchEventName: "Activity Impression Click",
    trackingViewEventName: "Activity Impression",
    trackingUniqueId: activity_uid,
  };
}

type MobileProps = {
  activity: ActivityListing_ListingFragment["activity"];
  nextSection: ActivityListing_ListingFragment["nextSection"];
  leader: ActivityListing_ListingFragment["leader"];
  sectionSearchFilters?: SectionSearchFilters;
  trackingProperties: TrackingProperties;
  disabled: boolean;
  insertionId?: ActivityListing_ListingFragment["insertionId"];
  openInSSRForLoggedOutUsers?: boolean;
  listingIndex?: number;
  pageLocation: string;
  variant?: ActivityListingProps["variant"];
  listingSectionOverride?: React.ReactNode;
  showCredits?: boolean;
  openInNewTab?: boolean;
  isMiniListingCard?: boolean;
  showCTAButton?: boolean;
};

type MobileOverrideVariantProps = {
  activity: ActivityListing_ListingFragment["activity"];
  leader: ActivityListing_ListingFragment["leader"];
  sectionSearchFilters?: SectionSearchFilters;
  trackingProperties: TrackingProperties;
  disabled: boolean;
  insertionId?: ActivityListing_ListingFragment["insertionId"];
  openInSSRForLoggedOutUsers?: boolean;
  listingIndex?: number;
  pageLocation: string;
  variant?: ActivityListingProps["variant"];
  listingSectionOverride?: React.ReactNode;
};

export const MobileListing = ({
  activity,
  nextSection,
  leader,
  sectionSearchFilters,
  trackingProperties,
  disabled,
  insertionId,
  openInSSRForLoggedOutUsers,
  listingIndex = -1,
  pageLocation,
  showCredits,
  openInNewTab,
  isMiniListingCard = false,
  showCTAButton = false,
}: MobileProps) => {
  const Link = useLinkComponent();
  const theme = useTheme();
  const { t } = useTranslation("ui-components-website\\Listings");

  // @ts-ignore TS(2339): Property 'path' does not exist on type '{ __typena... Remove this comment to see the full error message
  let activityPath = activity.path;

  if (!!sectionSearchFilters) {
    activityPath = addParamsToUrl(activityPath, sectionSearchFilters);
  }
  const { searchImpressionRef, trackSearchImpressionClick } =
    useLogSearchImpression({
      // @ts-ignore TS(2322): Type '{ __typename?: "Activity" | undefined; uid: ... Remove this comment to see the full error message
      listing: { activity, leader, insertionId },
      pageLocation,
      listingIndex,
    });

  const navigate = useNavigation();

  const onLinkClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    trackSearchImpressionClick();

    if (openInNewTab) {
      e.preventDefault();
      navigate(activityPath, { newTab: true, hardNav: true });
    }

    if (openInSSRForLoggedOutUsers) {
      e.preventDefault();
      // Hard navigate so a full page load opens SSR.
      navigate(activityPath, { hardNav: true });
    }
  };

  const { ageRangeString } = useActivityString();

  const isSaved = useIsActivitySavedByCurrentUser(activity);
  const pixelRatio = useDevicePixelRatio();
  const photo = FilestackUrls.imageUrl(activity.details.photo, {
    height: 90 * pixelRatio,
    width: 120 * pixelRatio,
    fit: "crop",
  });

  const {
    perClassString,
    classTypeString,
    perMeetingPriceString,
    detailedScheduleString,
  } = usePriceDetailsString();
  return (
    <Link
      to={activityPath}
      eventName="Activity Impression Click"
      {...trackingObject(activity?.uid, trackingProperties)}
      useLinkTracking
      tabIndex={disabled ? -1 : undefined}
      onClick={onLinkClick}
      style={{ textDecoration: "none" }}
      trackViews={true}
      data-test-id="activity-listing-link"
    >
      <Box
        flex
        id={activity.uid}
        ref={searchImpressionRef}
        sx={{
          borderBottom: "1px solid",
          borderColor: "grey.200",
          color: "grey.900",
          flexDirection: "column",
        }}
      >
        <Box
          flex
          sx={{
            flexDirection: "column",
            padding: "8px",
            gap: "8px",
          }}
        >
          <Box
            flex
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box
              flex
              sx={{
                flexDirection: "row",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {isOneOnOne(activity) ? (
                <Box flex>
                  <UserHeadshotImage
                    user={leader}
                    size={48}
                    sx={{ border: "1px solid", borderColor: "gray200" }}
                  />
                </Box>
              ) : (
                <Box
                  flex
                  sx={{
                    position: "relative",
                  }}
                >
                  <Image
                    width="48px"
                    height="48px"
                    sx={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "12px",
                      objectFit: "cover",
                      display: "block",
                    }}
                    src={photo}
                    // @ts-ignore TS(2322): Type '(() => void) | null' is not assignable to ty... Remove this comment to see the full error message
                    onLoad={
                      listingIndex === 0
                        ? () => {
                            measurePerformance("os-page-load", {
                              source: "image",
                            });
                          }
                        : null
                    }
                  />
                  <SaveHeart
                    isSet={isSaved}
                    activity={activity}
                    // @ts-ignore TS(2322): Type '{ __typename?: "Leader" | undefined; uid: st... Remove this comment to see the full error message
                    leader={leader}
                    unfilledColor="rgba(255,255,255,.8)"
                    sx={{
                      fontSize: "24px",
                      position: "absolute",
                      top: "6px",
                      right: "6px",
                    }}
                    twoTone
                  />
                </Box>
              )}
              <Typography
                variant="subtitle2"
                sx={(theme: Theme) => ({
                  [theme.breakpoints.up("md")]: {
                    // Override the s2 variant fontSize at md breakpoint
                    fontSize: "1.3rem",
                  },
                })}
              >
                {activity.titleTranslated}
              </Typography>
            </Box>
            {showCTAButton && !isMiniListingCard && (
              <TrackedButton
                trackingName="teacher_pull_class_recommendation_button_clicked"
                variant="contained"
                size="small"
                onClick={onLinkClick}
              >
                {t("View Class")}
              </TrackedButton>
            )}
          </Box>
          <Box flex sx={{ justifyContent: "space-between" }}>
            <LeaderDetailsListing
              leader={leader}
              sx={isMiniListingCard ? { width: "80%" } : {}}
            />
            <Box
              flex
              sx={{
                minWidth: "72px",
                flexGrow: 1,
                alignItems: "center",
                flexDirection: "row",
                justifyContent: "flex-end",
                gap: "8px",
              }}
            >
              <Box
                flex
                sx={{
                  flexDirection: "column",
                  textAlign: "right",
                }}
              ></Box>
              <Typography variant="body2" emphasized color={"grey.900"}>
                {showCredits ? (
                  <Box
                    sx={{
                      display: "flex",
                      marginTop: "1rem",
                    }}
                  >
                    <Box
                      sx={{
                        paddingRight: "0.5rem",
                      }}
                    >
                      <CreditIcon
                        fillColor={theme.palette.warning.main}
                        strokeColor={theme.palette.warning[700]}
                        sx={{ height: "2.4rem", width: "2.4rem" }}
                      />
                    </Box>
                    {OSSubscriptions.priceCentsToCredits(
                      centsPerLearnerPerMeeting(activity)
                    )}
                  </Box>
                ) : (
                  perMeetingPriceString(activity)
                )}
              </Typography>
              <Typography variant="body2" color={"grey.600"}>
                {perClassString(activity)}
              </Typography>
            </Box>
          </Box>
          {showCTAButton && isMiniListingCard && (
            <TrackedButton
              trackingName="teacher_pull_class_recommendation_button_clicked"
              variant="contained"
              size="small"
              onClick={onLinkClick}
            >
              {t("View Class")}
            </TrackedButton>
          )}
        </Box>
        {!isMiniListingCard && (
          <>
            <Box
              sx={{
                backgroundColor: "grey.50",
                padding: "8px 8px 0 8px",
              }}
            >
              <NextStartTime
                nextSectionStartTime={nextSection?.start_time}
                nextOngoingMeetingStartTime={
                  nextSection?.nextOngoingMeeting?.start_time
                }
              />
            </Box>

            <Box
              flex
              sx={{
                backgroundColor: "grey.50",
                color: "grey.600",
                paddingX: 16,
                height: "32px",
                justifyContent: "space-between",
              }}
            >
              <Box flex sx={{ gap: "4px", alignItems: "center" }}>
                <Icon icon={faUsersClass} />
                <Typography variant="caption">
                  {classTypeString(activity)}
                </Typography>
              </Box>
              <Box flex sx={{ gap: "4px", alignItems: "center" }}>
                <Icon icon={faCalendar} />
                <Typography variant="caption">
                  {detailedScheduleString(activity)}
                </Typography>
              </Box>
              <Box flex sx={{ gap: "4px", alignItems: "center" }}>
                <Icon icon={faBirthdayCake} />
                <Typography variant="caption">
                  {ageRangeString(activity, false)}
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Link>
  );
};

export const MobileListingOverrideVariant = ({
  activity,
  leader,
  sectionSearchFilters,
  trackingProperties,
  disabled,
  insertionId,
  openInSSRForLoggedOutUsers,
  listingIndex = -1,
  pageLocation,
  listingSectionOverride,
}: MobileOverrideVariantProps) => {
  const Link = useLinkComponent();

  // @ts-ignore TS(2339): Property 'path' does not exist on type '{ __typena... Remove this comment to see the full error message
  let activityPath = activity.path;

  if (!!sectionSearchFilters) {
    activityPath = addParamsToUrl(activityPath, sectionSearchFilters);
  }
  const { searchImpressionRef, trackSearchImpressionClick } =
    useLogSearchImpression({
      // @ts-ignore TS(2322): Type '{ __typename?: "Activity" | undefined; uid: ... Remove this comment to see the full error message
      listing: { activity, leader, insertionId },
      pageLocation,
      listingIndex,
    });

  const navigate = useNavigation();

  const onLinkClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    trackSearchImpressionClick();

    if (openInSSRForLoggedOutUsers) {
      e.preventDefault();
      // Hard navigate so a full page load opens SSR.
      navigate(activityPath, { hardNav: true });
    }
  };

  const isSaved = useIsActivitySavedByCurrentUser(activity);
  const pixelRatio = useDevicePixelRatio();
  const photo = FilestackUrls.imageUrl(activity.details.photo, {
    height: 90 * pixelRatio,
    width: 120 * pixelRatio,
    fit: "crop",
  });

  return (
    <>
      <Box
        flex
        id={activity.uid}
        ref={searchImpressionRef}
        sx={{
          borderBottom: "1px solid",
          borderColor: "grey.200",

          flexDirection: "column",
        }}
      >
        <Link
          to={activityPath}
          eventName="Activity Impression Click"
          {...trackingObject(activity?.uid, trackingProperties)}
          useLinkTracking
          tabIndex={disabled ? -1 : undefined}
          onClick={onLinkClick}
          style={{ textDecoration: "none", color: "black" }}
          trackViews={true}
        >
          <Box
            flex
            sx={{
              flexDirection: "column",
              padding: "8px",
              gap: "8px",
            }}
          >
            <Box
              flex
              sx={{
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <Box sx={{ maxWidth: "calc(60vw - 40px)" }}>
                <Typography
                  variant="subtitle2"
                  sx={(theme: Theme) => ({
                    [theme.breakpoints.up("md")]: {
                      // Override the s2 variant fontSize at md breakpoint
                      fontSize: "1.3rem",
                    },
                  })}
                >
                  {activity.titleTranslated}
                </Typography>

                <LeaderDetailsMobileListing leader={leader} />
              </Box>

              <Box
                flex
                sx={{
                  position: "relative",
                }}
              >
                <Image
                  sx={theme => ({
                    borderRadius: "12px",
                    objectFit: "contain",
                    display: "block",
                    [theme.breakpoints.down("sm")]: {
                      width: "calc(40vw - 40px)",
                    },
                  })}
                  src={photo}
                  // @ts-ignore TS(2322): Type '(() => void) | null' is not assignable to ty... Remove this comment to see the full error message
                  onLoad={
                    listingIndex === 0
                      ? () => {
                          measurePerformance("os-page-load", {
                            source: "image",
                          });
                        }
                      : null
                  }
                />
                <SaveHeart
                  isSet={isSaved}
                  activity={activity}
                  // @ts-ignore TS(2322): Type '{ __typename?: "Leader" | undefined; uid: st... Remove this comment to see the full error message
                  leader={leader}
                  unfilledColor="rgba(255,255,255,.8)"
                  sx={{
                    fontSize: "24px",
                    position: "absolute",
                    top: "6px",
                    right: "6px",
                  }}
                  twoTone
                />
              </Box>
            </Box>
          </Box>
        </Link>
        {listingSectionOverride}
      </Box>
    </>
  );
};
